// @flow
import {takeEvery, select, put, call} from 'redux-saga/effects';
import {CommonActions} from '@react-navigation/native';
import {tripica} from '../../api';
import {
  customerOuidSelector,
  customerTaxExemptionsSelector,
} from '../Customer/selectors';
import {SIMSerialNumberSelector} from '../SignUp/SIMCardScan';
import {
  currentSubscriptionBillingAccountOuidSelector,
  currentSubscriptionOuidSelector,
  selectSubscription,
  visibleSubscriptionsSelector,
} from '../Dashboard';
import {setCurrentSubscription} from '../Subscription/actions';
import {setLoading, setError} from '../LoadingStatus/actions';
import {triggerProductOrderPolling} from '../PollingOrder';
import type {ManageLineActionType} from './actions';
import type {errorMapForOrderStateType} from '../PollingOrder/lib';
import type {LoadingStatusStateType} from '../LoadingStatus';
import Navigation from '../../lib/navigation';
import {eSimSelectedSelector, simResourceSelector} from '../../modules/ESim/selectors';
import {eSimSelectedSuccess} from '../../modules/ESim/actions';
import {voucherCurrentVoucherDetailsSelector} from '../Voucher/selector';
import WizzeeLog from '../../lib/wizzeeLog';

export function* suspensionSwapTerminationSaga(
  action: ManageLineActionType,
): Generator<*, *, *> {
  if (action.payload.subscription) {
    yield put(setCurrentSubscription(action.payload.subscription));
  }
  const customerOuid = yield select(customerOuidSelector);
  const subscriptionOuid = yield select(currentSubscriptionOuidSelector);
  const billingAccountOuid = yield select(
    currentSubscriptionBillingAccountOuidSelector,
  );
  const methodArgs = [customerOuid, billingAccountOuid, subscriptionOuid];
  let redirectionRouteName;
  switch (action.type) {
    case 'SUSPEND_LINE':
      redirectionRouteName = 'suspendSuccess';
      // Forcing eSimSelected to be false
      yield put(eSimSelectedSuccess(false));
      yield call(
        callAndRedirect,
        'suspendContract',
        methodArgs,
        redirectionRouteName,
        action.payload.errorMapForOrderState,
      );
      break;
    case 'UNSUSPEND_LINE':
      redirectionRouteName = 'unsuspendSuccess';
      yield call(
        callAndRedirect,
        'unsuspendContract',
        methodArgs,
        redirectionRouteName,
        action.payload.errorMapForOrderState,
      );
      break;
    case 'TERMINATE_LINE':
      redirectionRouteName = 'terminateSuccess';

      // Forcing eSimSelected to be false
      yield put(eSimSelectedSuccess(false));

      const terminateCallSuccess = yield call(
        callAndRedirect,
        'terminateContract',
        methodArgs,
        redirectionRouteName,
        action.payload.errorMapForOrderState,
      );
      if (terminateCallSuccess) {
        const visibleSubscriptions = yield select(visibleSubscriptionsSelector);
        const nextSubscription = visibleSubscriptions.find(
          subscription => subscription.ouid !== subscriptionOuid,
        );
        if (nextSubscription) yield put(selectSubscription(nextSubscription));
      }
      break;
    case 'SWAP_SIM': {
      const eSimSelected = yield select(eSimSelectedSelector);
      const simReference = yield select(SIMSerialNumberSelector);
      const taxExemptions = yield select(customerTaxExemptionsSelector);
      const voucherDetails = yield select(voucherCurrentVoucherDetailsSelector);
      yield put(setLoading('confirmPlan', true));

      // Build simResource object for the new SDK signature
      const simResource = eSimSelected
        ? yield select(simResourceSelector)
        : {type: 'STANDARD_SIM', resourceSerialNumber: simReference};

      redirectionRouteName = 'swapActivateSuccess';
      yield call(
        callAndRedirect,
        'simSwap',
        [
          ...methodArgs,
          simResource,
          false,
          voucherDetails,
          taxExemptions,
        ],
        redirectionRouteName,
        action.payload.errorMapForOrderState,
      );
      yield put(setLoading('confirmPlan', false));
      break;
    }
    default:
      return;
  }
  if (redirectionRouteName)
    yield call(
      Navigation.dispatch,
      CommonActions.reset({
        key: null,
        index: 0,
        routes: [{name: redirectionRouteName}],
      }),
    );
}

export function* callAndRedirect(
  methodToCall: string,
  methodArgs: string[],
  redirectionRouteName: $Keys<LoadingStatusStateType>,
  errorMapForOrderState: errorMapForOrderStateType,
): Generator<*, *, *> {
  let productOrder;
  try {
    yield put(setLoading(redirectionRouteName, true));
    const response = yield call([tripica, methodToCall], ...methodArgs);
    if (!response || !response.errorCodeList) {
      throw new Error('No response or errorCodeList');
    }
    if (response.status === 'VALIDATED') {
      productOrder = yield call(
        triggerProductOrderPolling,
        response.uuid,
        errorMapForOrderState,
      );
    } else {
      throw new Error(response.errorCodeList.join(','));
    }
  } catch (e) {
    WizzeeLog.warn('[saga] suspendOrTerminateLineSaga', e);
    yield put(
      setError(errorMapForOrderState.key, errorMapForOrderState.type.FAILED),
    );
  } finally {
    yield put(setLoading(redirectionRouteName, false));
    return !!productOrder;
  }
}

export default function* (): Generator<*, *, *> {
  yield* [takeEvery('SUSPEND_LINE', suspensionSwapTerminationSaga)];
  yield* [takeEvery('TERMINATE_LINE', suspensionSwapTerminationSaga)];
  yield* [takeEvery('UNSUSPEND_LINE', suspensionSwapTerminationSaga)];
  yield* [takeEvery('SWAP_SIM', suspensionSwapTerminationSaga)];
}
